'use client'
import { useMemo } from 'react'
import { type CountryCode, type Language } from 'ecosystem'
import { countryNames } from 'shared-utils'
import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'
import { GrandFormSelectField } from './GrandFormSelectField'

interface CountryOption {
  value: CountryCode
  label: string
  [key: string]: unknown
}

interface GrandFormCountrySelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> {
  name: TName
  label?: string
  placeholder?: string
  required?: boolean
  optional?: boolean
  disabled?: boolean
  isClearable?: boolean
  isSearchable?: boolean
  /** Restrict the dropdown to a fixed list (e.g. the carrier's supported markets). */
  availableCountries?: CountryCode[]
  /** Locale used to render the country names. Defaults to `document.documentElement.lang` or `sv`. */
  locale?: Language
  rules?: RegisterOptions<TFieldValues, TName>
  control?: Control<TFieldValues>
}

/**
 * `forms/grand-form` country dropdown. Mirrors `CheckoutCountrySelect` but
 * plugs into a react-hook-form field via `GrandFormSelectField`. Options are
 * sourced from the shared `countryNames` dictionary, localised by `locale`,
 * and optionally filtered by `availableCountries`.
 */
export const GrandFormCountrySelect = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  availableCountries,
  locale: _locale,
  ...rest
}: GrandFormCountrySelectProps<TFieldValues, TName>) => {
  const options = useMemo<CountryOption[]>(() => {
    const locale = (_locale ??
      (typeof document !== 'undefined' ? document.documentElement.lang : 'sv') ??
      'sv') as Language
    let opts: CountryOption[] = Object.entries(countryNames[locale] ?? countryNames.sv).map(
      ([code, label]) => ({ value: code as CountryCode, label })
    )
    if (availableCountries?.length) {
      opts = opts.filter((o) => availableCountries.includes(o.value))
    }
    opts.sort((a, b) => a.label.localeCompare(b.label, locale))
    return opts
  }, [_locale, availableCountries])

  return (
    <GrandFormSelectField<TFieldValues, TName, CountryOption>
      isSearchable
      options={options}
      {...rest}
    />
  )
}
